import React, { useState, useContext } from 'react';
import {
  ThumbsUp,
  Heart,
  Zap,
  Smile,
  Frown,
  AlertCircle,
  Trash2,
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { addReaction, removeReaction } from '../services/programService';

const ReactionSelector = ({ programId, onClose }) => {
  const reactions = [
    { type: 'like', icon: ThumbsUp, label: 'Like', color: 'blue' },
    { type: 'love', icon: Heart, label: 'Love', color: 'red' },
    { type: 'care', icon: Zap, label: 'Care', color: 'green' },
    { type: 'wow', icon: AlertCircle, label: 'Wow', color: 'yellow' },
    { type: 'haha', icon: Smile, label: 'Haha', color: 'purple' },
    { type: 'sad', icon: Frown, label: 'Sad', color: 'gray' },
    { type: 'angry', icon: AlertCircle, label: 'Angry', color: 'orange' },
  ];

  const handleReaction = async (reactionType) => {
    try {
      await addReaction(programId, AuthContext, reactionType);
      onClose();
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  return (
    <div className="flex gap-2 bg-white rounded-full shadow-lg p-2 border border-gray-200">
      {reactions.map(({ type, icon: Icon, label, color }) => (
        <button
          key={type}
          onClick={() => handleReaction(type)}
          title={label}
          className={`p-2 rounded-full hover:scale-125 transition-transform bg-${color}-50 text-${color}-600 hover:bg-${color}-100`}
        >
          <Icon size={20} />
        </button>
      ))}
    </div>
  );
};

const ProgramCard = ({ program, onRefresh }) => {
  const { currentUser, userRole } = useContext(AuthContext);
  const [showReactionSelector, setShowReactionSelector] = useState(false);
  const [userReaction, setUserReaction] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);

  const isAdmin = ['master_admin', 'overseer', 'colony_prime_minister'].includes(userRole);

  const reactionData = [
    { type: 'like', icon: ThumbsUp, count: program.reactionsData?.like || 0, color: 'blue' },
    { type: 'love', icon: Heart, count: program.reactionsData?.love || 0, color: 'red' },
    { type: 'care', icon: Zap, count: program.reactionsData?.care || 0, color: 'green' },
    { type: 'wow', icon: AlertCircle, count: program.reactionsData?.wow || 0, color: 'yellow' },
    { type: 'haha', icon: Smile, count: program.reactionsData?.haha || 0, color: 'purple' },
    { type: 'sad', icon: Frown, count: program.reactionsData?.sad || 0, color: 'gray' },
    { type: 'angry', icon: AlertCircle, count: program.reactionsData?.angry || 0, color: 'orange' },
  ];

  const handleReaction = async (reactionType) => {
    if (!currentUser) {
      alert('Please sign in to react to programs');
      return;
    }

    setLoading(true);
    try {
      if (userReaction === reactionType) {
        await removeReaction(program.id, currentUser.uid);
        setUserReaction(null);
      } else {
        await addReaction(program.id, currentUser.uid, reactionType);
        setUserReaction(reactionType);
      }
      onRefresh();
    } catch (error) {
      console.error('Failed to update reaction:', error);
      alert('Failed to update reaction');
    } finally {
      setLoading(false);
      setShowReactionSelector(false);
    }
  };

  const handleDelete = async () => {
    if (!isAdmin) return;
    
    if (window.confirm('Are you sure you want to delete this program?')) {
      setDeleting(true);
      try {
        // Implementation for delete
        onRefresh();
      } catch (error) {
        console.error('Failed to delete program:', error);
      } finally {
        setDeleting(false);
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition">
      {/* Flyer Image */}
      <div className="relative">
        <img
          src={program.flyerUrl}
          alt={program.title}
          className="w-full h-64 object-cover"
        />
        {isAdmin && program.createdBy === currentUser.uid && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition disabled:opacity-50"
          >
            <Trash2 size={18} />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-lg font-bold text-gray-800 mb-2">{program.title}</h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{program.description}</p>

        {/* Reactions */}
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {reactionData.map(({ type, icon: Icon, count, color }) => (
              count > 0 && (
                <span
                  key={type}
                  className={`flex items-center gap-1 px-3 py-1 bg-${color}-50 text-${color}-700 rounded-full text-xs font-medium`}
                >
                  <Icon size={14} />
                  {count}
                </span>
              )
            ))}
          </div>

          {/* Reaction Buttons */}
          <div className="relative">
            <button
              onClick={() => setShowReactionSelector(!showReactionSelector)}
              disabled={loading || !currentUser}
              className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition disabled:opacity-50 text-sm font-medium"
            >
              {userReaction
                ? `You reacted: ${userReaction.charAt(0).toUpperCase() + userReaction.slice(1)}`
                : 'React to this program'}
            </button>

            {showReactionSelector && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-10">
                <div className="flex gap-1 bg-white rounded-full shadow-lg p-2 border border-gray-200">
                  {reactionData.map(({ type, icon: Icon, color }) => (
                    <button
                      key={type}
                      onClick={() => handleReaction(type)}
                      title={type}
                      className={`p-2 rounded-full hover:scale-125 transition-transform`}
                    >
                      <Icon size={20} className={`text-${color}-600`} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramCard;
